import React from 'react';
import NiceModal from '@ebay/nice-modal-react';
import {ModaleDetailsProduitCatalogue} from './ModaleDetailsProduitCatalogue';
import {
  Box,
  HStack,
  Link,
  Skeleton,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import {useTranslation} from 'react-i18next';
import {State} from '@synako/halitrek';
import {ArticleDesc} from 'api-types/ffconnect-hal';
import {LigneProduitLayout} from '../../liste/components/LigneProduitLayout';
import {ArticleBlockCatalogueGrandCompte} from '../../panier/PanierFound/ArticleBlockCatalogueGrandCompte';
import {Hit} from 'api-types/ffconnect';
import {CommandesIcon} from '@/components/svg/CommandesIcon';
import {HaliRenderOne, useHalState} from '@synako/haligator';
import {ArrowForwardIcon, DownloadIcon} from '@chakra-ui/icons';
import {getNombreSocietesDetentionAvecVente} from './detention';

interface LigneCatalogueGrandCompteProps {
  articleState: State<ArticleDesc> | undefined;
  hit: Hit;
  backoffice: boolean;
}

/**
 * Ligne représentant un article dans le catalogue grand compte
 */
export const LigneCatalogueGrandCompte: React.FC<
  LigneCatalogueGrandCompteProps
> = ({articleState, hit, backoffice}) => {
  const {t} = useTranslation();

  // Fallback: on prends les valeurs du hit si l'article n'est pas encore chargé
  const code_article =
    articleState?.data.tolede.code_article ?? hit.code_article;
  const libelle =
    articleState?.data.tolede.libelle_commercial ??
    articleState?.data.tolede.libelle ??
    hit.libelle_commercial;
  const unite_de_commande_client =
    articleState?.data.tolede.unite_de_commande_client; //?? hit.unite_de_commande_client;
  const unite_de_facturation = articleState?.data.tolede.unite_de_facturation; //?? hit.unite_de_facturation;
  const multiple_de_commande_client =
    articleState?.data.tolede.multiple_de_commande_client; //?? hit.multiple_de_commande_client;
  const coefficient_commande_client_vers_facturation =
    articleState?.data.tolede.coefficient_commande_client_vers_facturation; //?? hit.coefficient_commande_client_vers_facturation;

  const openModal = () => {
    NiceModal.show(ModaleDetailsProduitCatalogue, {
      hit,
      articleState,
      backoffice,
    });
  };

  const articleBlock = (
    <ArticleBlockCatalogueGrandCompte
      code_article={code_article}
      libelleFallback={libelle}
      onClick={openModal}
    />
  );

  const unitInfoBlock = (
    <HStack alignItems={'flex-start'}>
      <CommandesIcon color='gray.dark' />
      <VStack
        alignItems={'flex-start'}
        textAlign={'left'}
        spacing={0.5}
      >
        <Box
          color='gray.dark'
          fontSize='sm'
        >
          {t('grand_compte.conditionnement')}
        </Box>
        <Box>
          {unite_de_commande_client ? (
            unite_de_commande_client !== unite_de_facturation ? (
              <>
                {t('commande.par_multiple', {
                  quantite: multiple_de_commande_client,
                  unite: t(`unite.${unite_de_commande_client}`, {
                    count: multiple_de_commande_client,
                  }),
                })}
                &nbsp;
                {t('commande.de_multiple_facturation', {
                  multiple_facturation:
                    coefficient_commande_client_vers_facturation,
                  unite_facturation: t(`unite.${unite_de_facturation}`, {
                    count:
                      (coefficient_commande_client_vers_facturation ?? 1) *
                      (multiple_de_commande_client ?? 1),
                  }),
                })}
              </>
            ) : (
              <>
                {t('commande.par_multiple', {
                  quantite: multiple_de_commande_client,
                  unite: t(`unite.${unite_de_facturation}`, {
                    count: multiple_de_commande_client,
                  }),
                })}
              </>
            )
          ) : (
            <Skeleton
              width='50px'
              height='10px'
            />
          )}
        </Box>
      </VStack>
    </HStack>
  );

  const telechargerFicheBlock = () => {
    return (
      articleState && (
        <HaliRenderOne
          nav={articleState.follow('medias').follow('fiche_produit')}
          render={({state: ficheProduitState}) => (
            <HStack
              boxSize={'40px'}
              justifyContent='center'
              p={4}
              bg='gray.light'
              borderRadius={'8px'}
            >
              <Tooltip
                label={t('fiche_produit.fiche_produit_pdf.fiche_produit')}
                aria-label={t('fiche_produit.fiche_produit_pdf.fiche_produit')}
              >
                <Link
                  href={ficheProduitState.data.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                  _hover={{opacity: 0.8}}
                  aria-label={t(
                    'fiche_produit.fiche_produit_pdf.telecharger_fiche_produit',
                  )}
                >
                  <DownloadIcon
                    boxSize={4}
                    color='gray.dark'
                  />
                </Link>
              </Tooltip>
            </HStack>
          )}
          notFound={() => (
            <HStack
              boxSize={'40px'}
              justifyContent='center'
              p={4}
              bg='gray.100'
              borderRadius={'8px'}
            >
              <Tooltip
                label={t(
                  'fiche_produit.fiche_produit_pdf.fiche_produit_non_disponible',
                )}
                aria-label={t(
                  'fiche_produit.fiche_produit_pdf.fiche_produit_non_disponible',
                )}
              >
                <Box
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                >
                  <DownloadIcon
                    color='gray.300'
                    boxSize={4}
                  />
                </Box>
              </Tooltip>
            </HStack>
          )}
        />
      )
    );
  };

  const detentionBlock = () => {
    if (!articleState) return null;

    return (
      <HStack>
        {backoffice ? (
          // Vue backoffice : référence + détention avec nombre de sociétés
          <HStack
            bg='gray.light'
            borderRadius='12px'
            my={2}
            ml={{base: 0, md: 3}}
            p={{base: 2, md: 2}}
            px={{base: 5, md: 8}}
            spacing={8}
            justifyContent='center'
            alignItems='center'
            w={{base: 'auto', md: '13em'}}
          >
            <VStack spacing={1}>
              <Text
                color='gray.dark'
                fontSize='2xs'
              >
                {t('grand_compte.reference')}
              </Text>
              <Text
                color='gray.dark'
                fontSize='sm'
              >
                {t(
                  `grand_compte.reference_par.${articleState.data.tolede.reference_par}`,
                )}
              </Text>
            </VStack>
            <DetentionNombreSocietes
              articleState={articleState}
              onClick={openModal}
            />
          </HStack>
        ) : (
          // Vue client : référence
          <HStack
            bg='gray.light'
            borderRadius='12px'
            my={2}
            ml={4}
            p={2}
            px={6}
            spacing={4}
            justifyContent='center'
            alignItems='center'
            w={'auto'}
          >
            <VStack spacing={1}>
              <Text
                color='gray.dark'
                fontSize='2xs'
              >
                {t('grand_compte.reference')}
              </Text>
              <Text
                color='gray.dark'
                fontSize='sm'
              >
                {t(
                  `grand_compte.reference_par.${articleState.data.tolede.reference_par}`,
                )}
              </Text>
            </VStack>
          </HStack>
        )}

        <HStack
          justifyContent='flex-end'
          alignItems='center'
          cursor={'pointer'}
          onClick={openModal}
          ml={4}
        >
          <Box
            color='gray.dark'
            textDecorationLine={'underline'}
            whiteSpace={'nowrap'}
            fontSize='sm'
          >
            {t('grand_compte.voir_details')} <ArrowForwardIcon />
          </Box>
        </HStack>
      </HStack>
    );
  };

  return (
    <LigneProduitLayout
      flexWrap='wrap'
      p={{base: '2', md: '10px'}}
      py={{base: '2', md: '3px'}}
      gap={{base: 0, md: 2}}
    >
      <HStack
        align='start'
        w={{base: '80%', md: '30em'}}
      >
        <HStack align='center'>{articleBlock}</HStack>
      </HStack>

      <HStack
        align='center'
        ml={{base: 'auto', md: 0}}
      >
        {telechargerFicheBlock()}
      </HStack>

      <HStack
        ml='auto'
        alignItems='center'
        spacing={6}
        flex={1}
        justifyContent='flex-end'
        pr={{base: 0, md: 5}}
      >
        <HStack
          alignItems='center'
          w={{base: 'auto', md: '240px'}}
          justifyContent='flex-start'
          p={{base: '2', md: 'initial'}}
          pt='0'
        >
          {unitInfoBlock}
        </HStack>

        {backoffice && articleState?.data.tolede.est_TAC && (
          <HStack
            w={'55px'}
            justifyContent='center'
            p={3}
            py={2}
            bg='blue.main_transparency_10'
            color={'blue.main'}
            fontSize={'md'}
            borderRadius={'8px'}
          >
            <Box alignSelf='center'>TAC</Box>
          </HStack>
        )}
        {backoffice && !articleState?.data.tolede.est_TAC && <Box w={'80px'} />}

        {detentionBlock()}
      </HStack>
    </LigneProduitLayout>
  );
};

const DetentionNombreSocietes: React.FC<{
  articleState: State<ArticleDesc>;
  onClick: () => void;
}> = ({articleState, onClick}) => {
  const {t} = useTranslation();
  const detentionHook = useHalState(articleState.follow('detention'));
  const lignesDetention =
    detentionHook.state?.data.tolede.commercialise_par ?? [];
  const nombreSocietes = getNombreSocietesDetentionAvecVente(lignesDetention);

  return (
    <VStack spacing={1}>
      <Text
        color='gray.dark'
        fontSize='2xs'
      >
        {t('grand_compte.detention')}
      </Text>
      <HStack
        color='gray.dark'
        whiteSpace='nowrap'
        fontSize='sm'
        onClick={onClick}
        cursor='pointer'
        spacing={1}
      >
        {detentionHook.loading ? (
          <Skeleton
            width='60px'
            height='16px'
          />
        ) : (
          <Text>
            {t('grand_compte.nombre_societes', {count: nombreSocietes})}
          </Text>
        )}
      </HStack>
    </VStack>
  );
};
